// create CRUD class with axios
import axios, {type AxiosResponse} from "axios";
import ThermalPrinterBase from "../../services/thermalPrinter/ThermalPrinterBase";
// import ThermalPrinterBase from "./ThermalPrinterBase";
import dayjs from "dayjs";
import _ from "lodash"



import PrintCustomNiceloopV1Builder from "~/class/printer/PrintCustomNiceloopV1Builder";
import { useFormatNumber } from "~/composables/useUtils";
import { parsePaymentNames, formatPaymentType } from "~/utils/paymentType";
import { netSubtotalFromReceipt, receiptVat, aggregateReceiptVatSplit } from "~/utils/receiptDetailModal";

// Same rule as v2/reports/Orders.vue:isOnTheHouse — receipt customer didn't pay
// because additional discount equals subtotal (100% off / entertain).
function isOnTheHouseReceipt(r: any): boolean {
    if (r?.IsOnTheHouse === true) return true;
    return (Number(r?.GrandTotal) || 0) === 0
        && (Number(r?.Discount) || 0) > 0
        && (Number(r?.SubTotal) || 0) > 0;
}




export default class ThermalPrintSummaryIncome extends ThermalPrinterBase {  

    //#FROM NICELOOP POS
    printSummaryIncome(payload : any){  
        const summaryIncome =  this.withPayload(payload);
        console.log("summaryIncome=>", summaryIncome)
        this.print_data = summaryIncome
        return this
    }
   
    withPayload(payload:any){
        //excute receipts
       const goodBills = payload.receipts.filter((r: any) => !r?.IsDeleted);
       const voidBills = payload.receipts.filter((r: any) => r?.IsDeleted);

       // Report period start: prefer the shop-open time (Close Shop flow passes
       // startDate = OPEN_SHOP job timestamp). For historical reports we don't know
       // the open time, so fall back to the earliest receipt timestamp in the set.
       // Never hard-code 07:00 — that only matched BusinessMode 1 by coincidence.
       // This must NEVER throw: shops still need to sell/close even if the data is
       // missing or malformed. When nothing valid is found we leave startTime null
       // and the builder prints the safe 7:00 default.
       const resolveStartTime = (): string | null => {
           try {
               const isValidTs = (ts: any) => !!ts && dayjs(ts).isValid();
               // 1) shop-open job timestamp (Close Shop flow)
               if (isValidTs(payload?.startDate)) return payload.startDate;
               // 2) earliest valid receipt timestamp (historical reports)
               const getReceiptTime = (r: any) => r?.Date || r?.OrderDate || r?.createdAt || null;
               return (goodBills || []).reduce((earliest: string | null, r: any) => {
                   const ts = getReceiptTime(r);
                   if (!isValidTs(ts)) return earliest;
                   if (!earliest || dayjs(ts).isBefore(dayjs(earliest))) return ts;
                   return earliest;
               }, null as string | null);
           } catch (e) {
               console.warn("resolveStartTime failed, using default:", e);
               return null;
           }
       };
       const startTime = resolveStartTime();

       const guestsTotal = goodBills.reduce((total: number, receipt: any) => total + receipt.CustomerCount, 0);
       // NET subtotal from line items (Σ discountedPrice) — robust whether stored SubTotal
       // is net (current) or gross (after the write-path moves to gross/version 'B').
       // The builder adds ItemDiscount back to print the gross "Total", so feeding NET here
       // keeps it correct in every version (gross-stored + ItemDiscount would double-count).
       const subTotal = goodBills.reduce((total: number, receipt: any) => total + netSubtotalFromReceipt(receipt), 0);
       const grandTotal = goodBills.reduce((total: number, receipt: any) => total + receipt.GrandTotal, 0);
       // Recompute VAT fresh (version-aware) instead of trusting stored Vat — matches the
       // server report and v2/reports Orders summary.
       const vatTotal = goodBills.reduce((total: number, receipt: any) => total + receiptVat(receipt), 0);
       // Split VAT into Exclusive (added on top, positive) and Inclusive (embedded,
       // shown as a deduction/negative). A mixed day carries both — print separately.
       const vatSplit = aggregateReceiptVatSplit(goodBills);
       const itemsDiscountTotal = goodBills.reduce((total: number, receipt: any) => total + receipt.ItemDiscount, 0);
       const serviceChangeTotal = goodBills.reduce((total: number, receipt: any) => total + receipt.ServiceCharge, 0);
       const cardChargeTotal = goodBills.reduce((total: number, receipt: any) => total + receipt.CardCharge, 0);
       const discountBillTotal = goodBills.reduce((total: number, receipt: any) => total + receipt.Discount, 0);
       const voidedBillsAmount = voidBills.reduce((total: number, receipt: any) => total + receipt.GrandTotal, 0);

       const grandTotalByShift = this.computeGrandTotalByShift(goodBills)
       // Exclude on-the-house from byType — GrandTotal=0 distorts payment totals
       const paidBills = goodBills.filter((r: any) => !isOnTheHouseReceipt(r));
       const onTheHouseBills = goodBills.filter((r: any) => isOnTheHouseReceipt(r));
       const grandTotalByPaymentType = this.computeGrandTotalByPaymentType(paidBills)
       const onTheHouseSummary = this.computeOnTheHouseSummary(onTheHouseBills)
       const grandTotalByCashier = this.computeGrandTotalByCashier(goodBills)
       

        const newPayload ={
            shopName:payload.store.name,
            employee:payload.employee,
            startTime,
            goodBills,
            voidBills,       
            guests:guestsTotal,
            shiftsSummary:grandTotalByShift,
            paymentTypesSummary:grandTotalByPaymentType,
            onTheHouseSummary,
            cashiersSummary:grandTotalByCashier,
            drawerData:payload.drawerData, // wait for implement    
            billing:{
                subTotal:subTotal,
                itemsDiscountTotal:itemsDiscountTotal,
                serviceChangeTotal:serviceChangeTotal,
                discountBillTotal:discountBillTotal,
                cardChargeTotal:cardChargeTotal,
                vatTotal:vatTotal,
                vatExclusiveTotal:vatSplit.exclusive,
                vatInclusiveTotal:vatSplit.inclusive,
                grandTotal:grandTotal,
                voidedBillsAmount:voidedBillsAmount
            },
            isFullReport:payload.isFullReport,
            // Close Shop flow sets this so the report prints the "Close Shop" marker
            // (top + bottom) — distinguishes a real close-shop print from an interim
            // mid-day check that the owner ran without actually closing the shop.
            isCloseShop:payload.isCloseShop,
            // Change Shift flow re-uses the close-shop print path — this swaps the
            // banner wording (Close Shop → Change Shift) and triggers the outstanding
            // open-table line below.
            isChangeShift:payload.isChangeShift,
            // { count, total } of tables still open (unpaid) at the shift change —
            // printed so the incoming shift knows how much balance is carried over.
            outstanding:payload.outstanding,
            // Set when the close-shop receipts refresh from the server failed, so
            // the total may exclude other cashiers' sales — printed as a header warning.
            isLocalOnly:payload.isLocalOnly
        }

        console.log("newPayload=>", newPayload)

        const summaryIncome =  new PrintCustomNiceloopV1Builder().makeSummaryIncomeReport(newPayload).build();
        console.log("summaryIncome=>", summaryIncome)   
        return summaryIncome
    }

    setPayload(printPayload:any){
      this.print_data = printPayload
      return this
    }

    getBillArrValue(bill: any, index: number){
        return bill?.[index] || 0;
    }

    computeGrandTotalByShift(receipts:any){
        let shifts:any = {}
        receipts.forEach((receipt: any) => {
            const key = receipt.ShiftWork
            if(shifts[key]==undefined){
                shifts[key] = {
                    shift:receipt.ShiftWork,
                    total:0,
                    quantity:0
                }
            }

            shifts[key].total += receipt.GrandTotal
            shifts[key].quantity += 1
           
        })

        return Object.values(shifts)
    }

    computeGrandTotalByCashier(receipts:any){
        let cashiers:any = {}
        receipts.forEach((receipt: any) => {
            const key = receipt.User
            if(cashiers[key]==undefined){
                cashiers[key] = {
                    name:receipt.User,
                    total:0,
                    quantity:0
                }
            }

            cashiers[key].total += receipt.GrandTotal
            cashiers[key].quantity += 1
           
        })

        return Object.values(cashiers)
    }

    // Entertain / OnTheHouse: customer didn't pay (100% additional discount).
    // Aggregate by payment-name; amount = SubTotal (fallback to Discount).
    computeOnTheHouseSummary(receipts: any) {
        const buckets: Record<string, { name: string; total: number; quantity: number }> = {};
        receipts.forEach((receipt: any) => {
            const name = parsePaymentNames(receipt)[0] || formatPaymentType(receipt) || "OnTheHouse";
            const amount =  Number(receipt?.Discount) || 0;
            if (!buckets[name]) {
                buckets[name] = { name, total: 0, quantity: 0 };
            }
            buckets[name].total += amount;
            buckets[name].quantity += 1;
        });
        return Object.values(buckets);
    }

    computeGrandTotalByPaymentType(receipts:any){
        const paymentTypes:any = {}

        const upsert = (keyRaw: any, nameRaw: any, amountRaw: any, countedInReceipt: Set<string>) => {
            const key = String(keyRaw || nameRaw || "Unknown").trim() || "Unknown"
            const name = String(nameRaw || key).trim() || key
            const amount = Number(amountRaw || 0)

            if (paymentTypes[key] == undefined) {
                paymentTypes[key] = {
                    name,
                    type: key,
                    total: 0,
                    quantity: 0
                }
            }

            paymentTypes[key].total += amount
            if (!countedInReceipt.has(key)) {
                paymentTypes[key].quantity += 1
                countedInReceipt.add(key)
            }
        }

        receipts.forEach((receipt: any) => {
            const countedInReceipt = new Set<string>()

            // Case 1: explicit multiplePayment array
            if (Array.isArray(receipt?.multiplePayment) && receipt.multiplePayment.length > 0) {
                receipt.multiplePayment.forEach((payment: any) => {
                    upsert(payment?.type || payment?.name, payment?.name || payment?.type, payment?.amount, countedInReceipt)
                })
                return
            }

            // Backward-compatible: receipt.payment array
            if (Array.isArray(receipt?.payment) && receipt.payment.length > 0) {
                receipt.payment.forEach((payment: any) => {
                    upsert(payment?.type || payment?.name, payment?.name || payment?.type, payment?.amount, countedInReceipt)
                })
                return
            }

            // Case 2: PaymentType can be JSON string (multi) OR plain string (single)
            if (typeof receipt?.PaymentType === "string") {
                const raw = receipt.PaymentType.trim()

                if (raw.startsWith("[") && raw.endsWith("]")) {
                    try {
                        const parsed = JSON.parse(raw)
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            parsed.forEach((payment: any) => {
                                upsert(payment?.type || payment?.name, payment?.name || payment?.type, payment?.amount, countedInReceipt)
                            })
                            return
                        }
                    } catch (_) {
                        // fall through to single payment handling below
                    }
                }

                // single payment type, e.g. "Cash"
                if (raw) {
                    upsert(raw, raw, receipt?.GrandTotal, countedInReceipt)
                    return
                }
            }

            // fallback unknown
            upsert("Unknown", "Unknown", receipt?.GrandTotal, countedInReceipt)
        })

        return Object.values(paymentTypes)
    }
}
